import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LaunchIcon from "@mui/icons-material/Launch";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import { Navigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";

import SchoolAuthContext from "../../context/SchoolAuthContext";
import { useToast } from "../../hook/useToast";
import {
  fetchSchoolPortalDashboard,
  fetchSchoolPortalProfile,
  updateSchoolPortalProfile,
} from "../../services/schoolPortalApi";
import SchoolDashboardLayout from "./SchoolDashboardLayout";

const SECTION_LABELS = {
  dashboard: "Welcome to School Dashboard",
  profile: "Profile",
};

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;
const DEFAULT_SCHOOL_PROFILE_DESCRIPTION =
  "A focused school profile for managing students, reports, and counselling coordination from one secure portal.";

const STUDENT_COLUMNS = [
  { id: "sr", label: "Sr No.", align: "left", width: 78 },
  { id: "name", label: "Student name", align: "left", width: 260 },
  { id: "class", label: "Class", align: "center", width: 90 },
  { id: "city", label: "City", align: "left", width: 170 },
  { id: "board", label: "Board", align: "left", width: 130 },
  { id: "gmail", label: "Gmail ID", align: "left", width: 240 },
  { id: "report", label: "Report Link", align: "center", width: 132 },
];

const getDisplayValue = (value, fallback = "Not available") => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return fallback;
  }

  return value;
};

const formatNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return "--";
  }

  const number = Number(value);
  return Number.isNaN(number) ? value : number.toLocaleString("en-IN");
};

const getInitials = (name = "") => {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  return initials.toUpperCase() || "S";
};

const isFileObject = (value) =>
  typeof File !== "undefined" && value instanceof File;

const normalizeMediaUrl = (value) => {
  const url = String(value || "").trim();
  if (!url || /^(https?:|blob:|data:)/i.test(url)) {
    return url;
  }

  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(url, `${origin}/`).href;
  } catch {
    return url;
  }
};

const buildSchoolProfilePayload = (draft) => {
  const payload = new FormData();
  payload.append("school_name", (draft.school_name || draft.name || "").trim());
  payload.append("board", (draft.board || "").trim());
  payload.append("city", (draft.city || "").trim());

  if (isFileObject(draft.photo)) {
    payload.append("photo", draft.photo);
  }

  return payload;
};

const ICON_MAP = {
  total: GroupsOutlinedIcon,
  month: CalendarMonthOutlinedIcon,
  today: EventAvailableOutlinedIcon,
};

const StatsCard = ({ value, label, icon = "total", bgColor = "#F5F6FA" }) => {
  const Icon = ICON_MAP[icon] || ICON_MAP.total;

  return (
    <Paper
      elevation={0}
      sx={{
        minHeight: 100,
        borderRadius: "8px",
        bgcolor: bgColor,
        px: 2,
        py: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        border: "1px solid #EFEFF4",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 800,
            lineHeight: 1,
            color: "#111827",
            mb: 0.8,
          }}
        >
          {value ?? "--"}
        </Typography>

        <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#687083" }}>
          {label}
        </Typography>
      </Box>

      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "8px",
          bgcolor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0px 18px 32px rgba(45, 49, 95, 0.10)",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 30, color: "#111827" }} />
      </Box>
    </Paper>
  );
};

StatsCard.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  bgColor: PropTypes.string,
};

const tableHeaderCellSx = (column) => ({
  width: column.width,
  height: 45,
  py: 0,
  pl: column.align === "left" ? 2.5 : 1.5,
  pr: 1.5,
  fontSize: 12,
  fontWeight: 600,
  color: "#6B7280",
  borderBottom: "1px solid #EFEFF4",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const selectSx = {
  height: 38,
  borderRadius: "7px",
  fontSize: 13,
  color: "#2D315F",
  bgcolor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E4E6EF" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E4E6EF" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#D7DAE5" },
  "& .MuiSelect-select": { py: 0, display: "flex", alignItems: "center" },
};

const DashboardSection = () => {
  const { schoolUser } = useContext(SchoolAuthContext);
  const { showToast } = useToast();
  const [dashboard, setDashboard] = useState(schoolUser?.dashboard || {});
  const [statsLoading, setStatsLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  });
  const statsRequestIdRef = useRef(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    const requestId = statsRequestIdRef.current + 1;
    statsRequestIdRef.current = requestId;
    setStatsLoading(true);

    fetchSchoolPortalDashboard({ page, pageSize, search: debouncedSearch })
      .then((response) => {
        if (requestId !== statsRequestIdRef.current) return;
        const data = response?.data?.data || {};
        setDashboard(data);

        setStudents(Array.isArray(data.students) ? data.students : []);
        setPagination({
          page: data.pagination?.page || page,
          page_size: data.pagination?.page_size || pageSize,
          total_records: data.pagination?.total_records || 0,
          total_pages: data.pagination?.total_pages || 0,
        });
        setStudentsError("");
      })
      .catch((error) => {
        if (requestId !== statsRequestIdRef.current) return;

        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Could not load school dashboard.";

        showToast(apiMessage, "error");
        setStudents([]);
      })
      .finally(() => {
        if (requestId === statsRequestIdRef.current) {
          setStatsLoading(false);
          setStudentsLoading(false);
        }
      });
  }, [debouncedSearch, page, pageSize, showToast]);

  const totalPages = Math.max(1, pagination.total_pages || 1);
  const totalRecords = pagination.total_records || 0;
  const showingFrom = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, totalRecords);

  const paginationItems = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const items = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);

    if (start > 2) {
      items.push("dots-start");
    }

    for (let index = start; index <= end; index += 1) {
      items.push(index);
    }

    if (end < totalPages - 1) {
      items.push("dots-end");
    }

    items.push(totalPages);
    return items;
  }, [page, totalPages]);

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#171725" }}>
          Dashboard
        </Typography>
        {statsLoading ? (
          <CircularProgress size={16} sx={{ color: "#2D315F" }} />
        ) : null}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        <StatsCard
          value={statsLoading ? "--" : formatNumber(dashboard.total_students)}
          label="Total Students"
          icon="total"
          bgColor="#F5F6FA"
        />
        <StatsCard
          value={statsLoading ? "--" : formatNumber(dashboard.students_this_month)}
          label="Students This Month"
          icon="month"
          bgColor="#FFF9F1"
        />
        <StatsCard
          value={statsLoading ? "--" : formatNumber(dashboard.students_today)}
          label="Students Today"
          icon="today"
          bgColor="#F2F6FF"
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "10px",
          bgcolor: "#fff",
          border: "1px solid #EFEFF4",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            minHeight: 60,
            px: 2,
            py: 1.4,
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            borderBottom: "1px solid #EFEFF4",
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#171725" }}>
            Student List
          </Typography>

          <TextField
            placeholder="Search..."
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              width: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "7px",
                fontSize: 13,
                bgcolor: "#fff",
                "& fieldset": { borderColor: "#E4E6EF" },
                "&:hover fieldset": { borderColor: "#E4E6EF" },
                "&.Mui-focused fieldset": { borderColor: "#D7DAE5" },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {studentsLoading && search.trim() !== debouncedSearch ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SearchIcon sx={{ fontSize: 19, color: "#2D315F" }} />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {studentsError ? (
          <Box sx={{ px: 2, pt: 2 }}>
            <Alert severity="error">{studentsError}</Alert>
          </Box>
        ) : null}

        <TableContainer
          sx={{
            overflowX: "auto",
            "&::-webkit-scrollbar": { height: 8 },
            "&::-webkit-scrollbar-track": { bgcolor: "#F2F3F7" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#C6CAD8",
              borderRadius: 20,
            },
          }}
        >
          <Table sx={{ minWidth: 1100, tableLayout: "fixed" }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#FAFBFD" }}>
                {STUDENT_COLUMNS.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={tableHeaderCellSx(column)}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {studentsLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={STUDENT_COLUMNS.length}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={STUDENT_COLUMNS.length}
                    align="center"
                    sx={{ py: 8, color: "#92929D", fontSize: 14 }}
                  >
                    {debouncedSearch
                      ? "No students match your search"
                      : "No students found"}
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student) => {
                  const name = student.student_name || "Unnamed Student";
                  const gmail = student.gmail_id || "";

                  return (
                    <TableRow
                      key={student.student_id}
                      sx={{
                        height: 64,
                        "& td": { borderBottom: "1px solid #EFEFF4" },
                        "&:hover": { bgcolor: "#FCFCFE" },
                      }}
                    >
                      <TableCell
                        sx={{
                          pl: 3,
                          fontSize: 13,
                          color: "#111827",
                          fontWeight: 600,
                        }}
                      >
                        {formatNumber(student.sr_no)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: "50%",
                              bgcolor: "#EEF2FF",
                              color: "#2D315F",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 12,
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(name)}
                          </Box>
                          <Typography
                            title={name}
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#171725",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontSize: 13, color: "#4A4F63" }}
                      >
                        {student.class ?? "--"}
                      </TableCell>
                      <TableCell
                        title={student.city || ""}
                        sx={{
                          fontSize: 13,
                          color: "#4A4F63",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {student.city || "--"}
                      </TableCell>
                      <TableCell
                        title={student.board || ""}
                        sx={{
                          fontSize: 13,
                          color: "#4A4F63",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {student.board || "--"}
                      </TableCell>
                      <TableCell
                        title={gmail}
                        sx={{
                          fontSize: 13,
                          color: "#4A4F63",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          pr: 2.5,
                        }}
                      >
                        {gmail || "--"}
                      </TableCell>
                      <TableCell align="center">
                        {student.report_link ? (
                          <Box
                            component="a"
                            href={student.report_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 32,
                              height: 32,
                              borderRadius: "8px",
                              color: "#2D315F",
                              bgcolor: "#F5F6FA",
                              transition: "all 0.18s ease",
                              "&:hover": { bgcolor: "#E5E8F0", color: "#171725" },
                            }}
                            aria-label={`Open report for ${name}`}
                          >
                            <LaunchIcon sx={{ fontSize: 16 }} />
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: 13, color: "#B8BBC8" }}>
                            --
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            minHeight: 54,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 2,
            py: 1,
            bgcolor: "#fff",
            borderTop: totalRecords > 0 ? "1px solid #EFEFF4" : "none",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
            <Typography sx={{ fontSize: 13, color: "#171725", fontWeight: 600 }}>
              Total : {formatNumber(totalRecords)}
            </Typography>

            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              IconComponent={KeyboardArrowDownIcon}
              sx={{ ...selectSx, width: 110 }}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>

            <Typography sx={{ fontSize: 12, color: "#92929D", display: { xs: "none", sm: "block" } }}>
              Showing {showingFrom}-{showingTo}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              disabled={page === 1 || studentsLoading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <KeyboardArrowLeftIcon
                sx={{ fontSize: 20, color: page === 1 ? "#B8BBC8" : "#2D315F" }}
              />
            </IconButton>

            {paginationItems.map((item, index) => {
              if (String(item).startsWith("dots")) {
                return (
                  <Typography
                    key={`${item}-${index}`}
                    sx={{ fontSize: 14, color: "#171725", px: 0.5 }}
                  >
                    ...
                  </Typography>
                );
              }

              const selected = item === page;

              return (
                <Box
                  key={item}
                  onClick={() => {
                    if (!studentsLoading) {
                      setPage(item);
                    }
                  }}
                  sx={{
                    width: selected ? 32 : 28,
                    height: selected ? 32 : 28,
                    borderRadius: selected ? "50%" : "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: selected ? "#2D315F" : "transparent",
                    color: selected ? "#fff" : "#171725",
                    fontSize: 13,
                    fontWeight: selected ? 600 : 400,
                    cursor: studentsLoading ? "default" : "pointer",
                  }}
                >
                  {item}
                </Box>
              );
            })}

            <IconButton
              size="small"
              disabled={page >= totalPages || studentsLoading}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            >
              <KeyboardArrowRightIcon
                sx={{
                  fontSize: 20,
                  color: page >= totalPages ? "#B8BBC8" : "#2D315F",
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const ProfileField = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: 12,
        color: "#92929D",
        fontWeight: 600,
        mb: 0.6,
      }}
    >
      {label} :
    </Typography>
    <Typography
      sx={{
        fontSize: 14,
        color: "#171725",
        fontWeight: 700,
        overflowWrap: "anywhere",
      }}
    >
      {getDisplayValue(value)}
    </Typography>
  </Box>
);

ProfileField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const PROFILE_BANNER_GRADIENT =
  "linear-gradient(120deg, #FFA726 0%, #FFB74D 45%, #FFCC80 100%)";

const ProfileBannerCover = () => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      backgroundImage: PROFILE_BANNER_GRADIENT,
      overflow: "hidden",
    }}
  >
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        right: -40,
        bottom: -60,
        width: 320,
        height: 220,
        background:
          "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.35), rgba(255,255,255,0) 60%)",
        transform: "rotate(-12deg)",
      }}
    />
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        right: 36,
        top: 18,
        width: 90,
        height: 90,
        borderRadius: "50%",
        background:
          "radial-gradient(circle at 35% 35%, #FFE7B0, #FFB74D 60%, rgba(255,183,77,0) 75%)",
        filter: "blur(0.5px)",
      }}
    />
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        right: 110,
        top: 70,
        width: 12,
        height: 110,
        background: "#E53935",
        transform: "rotate(28deg)",
        borderRadius: 2,
        boxShadow: "inset -2px 0 0 rgba(0,0,0,0.18), inset 4px 0 0 #FFF3E0",
      }}
    />
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        right: 90,
        top: 56,
        width: 12,
        height: 110,
        background: "#FFCA28",
        transform: "rotate(22deg)",
        borderRadius: 2,
        boxShadow: "inset -2px 0 0 rgba(0,0,0,0.18), inset 4px 0 0 #FFF8E1",
      }}
    />
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        right: 70,
        top: 44,
        width: 12,
        height: 110,
        background: "#43A047",
        transform: "rotate(16deg)",
        borderRadius: 2,
        boxShadow: "inset -2px 0 0 rgba(0,0,0,0.18), inset 4px 0 0 #E8F5E9",
      }}
    />
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        right: 50,
        top: 36,
        width: 12,
        height: 110,
        background: "#1E88E5",
        transform: "rotate(10deg)",
        borderRadius: 2,
        boxShadow: "inset -2px 0 0 rgba(0,0,0,0.18), inset 4px 0 0 #E3F2FD",
      }}
    />
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        right: 18,
        top: 60,
        width: 110,
        height: 70,
        background: "#F1F2F6",
        transform: "rotate(20deg) skewX(-18deg)",
        borderRadius: "6px",
        boxShadow: "0 8px 18px rgba(0,0,0,0.12)",
      }}
    />
  </Box>
);

const PROFILE_DIALOG_PRIMARY = "#292E5C";
const PROFILE_DIALOG_SECONDARY = "#F7AA47";
const PROFILE_DIALOG_BORDER = "#D8DBDF";
const PROFILE_DIALOG_PLACEHOLDER = "#667085";

const profileDialogLabelSx = {
  color: "#000000",
  fontSize: 14,
  fontFamily: "Poppins, sans-serif",
  fontWeight: 400,
  lineHeight: "20px",
  mb: "4px",
};

const profileDialogInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 34,
    bgcolor: "#FFFFFF",
    borderRadius: "7px",
    fontSize: 14,
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    lineHeight: "20px",
    boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",

    "& fieldset": {
      borderColor: PROFILE_DIALOG_BORDER,
      borderWidth: "1px",
    },

    "&:hover fieldset": {
      borderColor: PROFILE_DIALOG_PRIMARY,
    },

    "&.Mui-focused fieldset": {
      borderColor: PROFILE_DIALOG_PRIMARY,
      borderWidth: "1px",
    },

    "& input": {
      px: "11px",
      py: "7px",
      color: "#171717",

      "&::placeholder": {
        color: PROFILE_DIALOG_PLACEHOLDER,
        opacity: 1,
      },
    },
  },
};

const ProfileEditDialog = ({ open, profile, saving, onClose, onSave }) => {
  const [draft, setDraft] = useState(profile || {});
  const [photo, setPhoto] = useState(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setDraft(profile || {});
      setPhoto(null);
    }
  }, [open, profile]);

  const existingPhotoUrl = normalizeMediaUrl(
    draft?.photo_url || draft?.photo || "",
  );

  const photoPreview = useMemo(() => {
    if (!photo) return existingPhotoUrl;

    if (isFileObject(photo)) {
      return URL.createObjectURL(photo);
    }

    if (typeof photo === "string") {
      return normalizeMediaUrl(photo);
    }

    return existingPhotoUrl;
  }, [existingPhotoUrl, photo]);

  useEffect(() => {
    return () => {
      if (photoPreview && isFileObject(photo)) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photo, photoPreview]);

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      setPhoto(file);
    }
    event.target.value = "";
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave({ ...draft, photo });
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const displayName =
    (draft?.school_name || draft?.name || "").trim() || "@School Name";

  const getDialogInitial = (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return "S";
    const parts = trimmed.split(" ").filter(Boolean).slice(0, 2);
    return parts.map((part) => part[0]).join("").toUpperCase() || "S";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
        sx: {
          width: 610,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "92vh",
          bgcolor: "#FFFFFF",
          borderRadius: "8px",
          boxShadow: "0px 16px 75px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          minHeight: 40,
          px: "15px",
          py: "8px",
          bgcolor: PROFILE_DIALOG_PRIMARY,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: "#FFFFFF",
            fontSize: 15,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 600,
            wordBreak: "break-word",
          }}
        >
          Edit/Update Profile
        </Typography>

        <IconButton
          type="button"
          aria-label="Close edit profile modal"
          onClick={handleClose}
          disabled={saving}
          sx={{
            width: 32,
            height: 32,
            color: "#FFFFFF",
            p: 0,
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: "24px",
          pt: "18px",
          pb: "18px",
          mt: "20px",
          bgcolor: "#FFFFFF",
          overflowY: "auto",

          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.28)",
            borderRadius: 10,
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "188px minmax(0, 1fr)" },
            gap: "12px",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: "100%",
              px: "14px",
              py: "13px",
              bgcolor: "rgba(80, 94, 85, 0.06)",
              borderRadius: "7px",
              border: "1px solid #EAECF0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              position: { md: "sticky" },
              top: { md: 0 },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                textAlign: "center",
                color: "#000000",
                fontSize: 14,
                fontFamily: "Poppins, sans-serif",
                fontWeight: 500,
                wordBreak: "break-word",
              }}
            >
              Upload Photo
            </Typography>

            <Box
              component="button"
          type="button"
          onClick={() => {
            if (!saving) photoInputRef.current?.click();
          }}
          sx={{
                width: 142,
                height: 142,
                p: 0,
                m: 0,
                border: "none",
                borderRadius: "50%",
                bgcolor: "rgba(41, 46, 92, 0.14)",
                cursor: saving ? "not-allowed" : "pointer",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                font: "inherit",
              }}
            >
              <Avatar
                src={photoPreview || undefined}
                sx={{
                  width: 134,
                  height: 134,
                  bgcolor: "#D9D9D9",
                  color: PROFILE_DIALOG_PRIMARY,
                  fontSize: 34,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 700,
                }}
              >
                {!photoPreview ? getDialogInitial(displayName) : null}
              </Avatar>

              <Box
                sx={{
                  width: 28,
                  height: 28,
                  position: "absolute",
                  right: 6,
                  bottom: 10,
                  bgcolor: PROFILE_DIALOG_SECONDARY,
                  borderRadius: "50%",
                  border: "1px solid #EAECF0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PhotoCameraOutlinedIcon
                  sx={{ fontSize: 16, color: "#FFFFFF" }}
                />
              </Box>
            </Box>

            <input
              ref={photoInputRef}
              hidden
              type="file"
              accept="image/*"
              disabled={saving}
              onChange={handlePhotoChange}
            />

            <Typography
              sx={{
                width: "100%",
                textAlign: "center",
                color: "#000000",
                fontSize: 14,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 700,
                lineHeight: "18px",
                wordBreak: "break-word",
              }}
            >
              {displayName}
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <Box>
              <Typography sx={profileDialogLabelSx}>School Name</Typography>
              <TextField
                fullWidth
                value={draft?.school_name || draft?.name || ""}
                placeholder="Enter school name"
                onChange={handleFieldChange("school_name")}
                sx={profileDialogInputSx}
              />
            </Box>

            <Box>
              <Typography sx={profileDialogLabelSx}>Board</Typography>
              <TextField
                fullWidth
                value={draft?.board || ""}
                placeholder="Enter board"
                onChange={handleFieldChange("board")}
                sx={profileDialogInputSx}
              />
            </Box>

            <Box>
              <Typography sx={profileDialogLabelSx}>City</Typography>
              <TextField
                fullWidth
                value={draft?.city || ""}
                placeholder="Enter your City"
                onChange={handleFieldChange("city")}
                sx={profileDialogInputSx}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: "32px",
          py: "15px",
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #E0E0E0",
          display: "flex",
          justifyContent: "flex-end",
          gap: "16px",
          flexShrink: 0,
        }}
      >
        <Button
          type="button"
          onClick={handleClose}
          variant="outlined"
          disabled={saving}
          sx={{
            px: "28px",
            py: "7px",
            minWidth: 110,
            borderRadius: "7px",
            borderColor: "#B5B5B5",
            color: "#B5B5B5",
            fontSize: 16,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            textTransform: "none",
            lineHeight: "normal",
            "&:hover": {
              borderColor: "#B5B5B5",
              bgcolor: "rgba(181,181,181,0.06)",
            },
          }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          sx={{
            width: 96,
            px: "26px",
            py: "7px",
            borderRadius: "7px",
            bgcolor: PROFILE_DIALOG_SECONDARY,
            color: "#FDFDFD",
            fontSize: 16,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            textTransform: "none",
            lineHeight: "normal",
            boxShadow: "none",
            "&:hover": { bgcolor: "#E99A32", boxShadow: "none" },
            "&.Mui-disabled": {
              bgcolor: "rgba(247, 170, 71, 0.55)",
              color: "#FFFFFF",
            },
          }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : "OK"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ProfileEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  profile: PropTypes.object,
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const ProfileSection = () => {
  const { schoolUser, updateSchoolProfile } = useContext(SchoolAuthContext);
  const { showToast } = useToast();
  const [profile, setProfile] = useState(schoolUser?.school || {});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    fetchSchoolPortalProfile()
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        const payload = response?.data?.data || {};
        setProfile(payload);
        updateSchoolProfile(payload);
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;

        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Could not load school profile.";

        showToast(apiMessage, "error");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [showToast, updateSchoolProfile]);

  const schoolName = profile.school_name || profile.name || "School";
  const schoolEmail = profile.email || "";
  const schoolPhotoUrl = normalizeMediaUrl(profile.photo_url || profile.photo);
  const schoolDescription =
    profile.description || DEFAULT_SCHOOL_PROFILE_DESCRIPTION;
  const schoolInitial = (schoolName || "S").trim()[0]?.toUpperCase() || "S";

  const handleEditSave = async (draft) => {
    const schoolNameValue = (draft.school_name || draft.name || "").trim();
    if (!schoolNameValue) {
      showToast("School name is required.", "error");
      return;
    }

    setSaving(true);

    try {
      const response = await updateSchoolPortalProfile(
        buildSchoolProfilePayload(draft),
      );
      const updatedProfile = response?.data?.data || {};
      setProfile(updatedProfile);
      updateSchoolProfile(updatedProfile);
      setEditOpen(false);
      showToast("Profile updated successfully", "success");
    } catch (error) {
      const fieldErrors = error?.response?.data?.errors;
      const firstFieldError = fieldErrors
        ? Object.values(fieldErrors).flat()[0]
        : null;
      const apiMessage =
        firstFieldError ||
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        "Could not update school profile.";

      showToast(apiMessage, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          borderRadius: "14px",
          border: "1px solid #EFEFF4",
          overflow: "hidden",
          bgcolor: "#fff",
          boxShadow: "0 8px 22px rgba(45, 49, 95, 0.06)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: 170,
            width: "100%",
          }}
        >
          <ProfileBannerCover />
          {loading ? (
            <Box
              sx={{
                position: "absolute",
                top: 14,
                right: 14,
                bgcolor: "rgba(255,255,255,0.7)",
                borderRadius: "50%",
                p: 0.4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={18} sx={{ color: "#2D315F" }} />
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            px: { xs: 2.5, md: 3.5 },
            pt: 0,
            pb: 2.5,
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "flex-end" },
              justifyContent: "space-between",
              gap: { xs: 2, md: 2 },
              mt: { xs: -6, md: -7 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
              <Avatar
                src={schoolPhotoUrl || undefined}
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "12px",
                  bgcolor: "#C7CCD9",
                  color: "#475058",
                  fontSize: 36,
                  fontWeight: 800,
                  border: "4px solid #fff",
                  boxShadow: "0 6px 16px rgba(45, 49, 95, 0.18)",
                  flexShrink: 0,
                }}
              >
                {!schoolPhotoUrl ? schoolInitial : null}
              </Avatar>

              <Box sx={{ pb: { xs: 0, md: 0.5 }, pt: { xs: 1, md: 6 } }}>
                <Typography
                  sx={{
                    fontSize: { xs: 18, md: 20 },
                    fontWeight: 800,
                    color: "#171725",
                    lineHeight: 1.2,
                  }}
                >
                  @{schoolName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#92929D",
                    mt: 0.4,
                  }}
                >
                  {getDisplayValue(schoolEmail, "username@gmail.com")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#667085",
                    mt: 1.2,
                    maxWidth: 540,
                  }}
                >
                  {schoolDescription}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setEditOpen(true)}
              sx={{
                alignSelf: { xs: "stretch", md: "flex-end" },
                mb: { xs: 0, md: 1.5 },
                bgcolor: "#FFB547",
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "10px",
                px: 2.4,
                py: 1,
                boxShadow: "0 8px 18px rgba(255, 181, 71, 0.35)",
                "&:hover": { bgcolor: "#FFA726" },
              }}
            >
              Edit / Update
            </Button>
          </Box>

          <Box
            sx={{
              mt: 3,
              borderBottom: "1px solid #EFEFF4",
              display: "flex",
              gap: 3,
              overflowX: "auto",
            }}
          >
            <Box
              sx={{
                position: "relative",
                pb: 1.2,
                fontSize: 14,
                fontWeight: 700,
                color: "#2D315F",
                whiteSpace: "nowrap",
              }}
            >
              Basic Information
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: -1,
                  height: 2,
                  bgcolor: "#FFB547",
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          borderRadius: "14px",
          border: "1px solid #EFEFF4",
          bgcolor: "#F4F6FB",
          boxShadow: "0 6px 18px rgba(45, 49, 95, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 1.8,
            borderBottom: "1px solid #EFEFF4",
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#171725" }}>
            Basic Information
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 2.5 }}>
          <Grid container spacing={{ xs: 2, md: 4 }} rowSpacing={2.5}>
            <Grid item xs={12} sm={6}>
              <ProfileField
                label="School Name"
                value={profile.school_name || profile.name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Board" value={profile.board} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="City" value={profile.city} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Pincode" value={profile.pincode} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Contact Number" value={profile.contact_number} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Status" value={profile.status} />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <ProfileEditDialog
        open={editOpen}
        profile={profile}
        saving={saving}
        onClose={() => setEditOpen(false)}
        onSave={handleEditSave}
      />
    </Box>
  );
};

const SchoolDashboardPage = () => {
  const { section } = useParams();
  const activeSection = section || "dashboard";

  if (!SECTION_LABELS[activeSection]) {
    return <Navigate to="/school-portal/dashboard" replace />;
  }

  const renderSection = () => {
    if (activeSection === "profile") {
      return <ProfileSection />;
    }

    return <DashboardSection />;
  };

  return (
    <SchoolDashboardLayout title={SECTION_LABELS[activeSection]}>
      {renderSection()}
    </SchoolDashboardLayout>
  );
};

export default SchoolDashboardPage;
